/**
 * What the voice comparison takes beside the pair itself: the one transport over the two clips,
 * the decisions an analyst may take, and where a pair stands as the screen reads it. The pair
 * and its sides are typed in `voice-pair-types.ts`.
 */
import type { PlaybackState } from '../../questioning/questioning-types'
import type { RecordingAudioState } from '../../questioning/recording-types'
import type { SubjectPageProblem } from '../../subject/SubjectPageNotice'
import type { VoicePair, VoicePairSideId } from './voice-pair-types'

/** The two sides in the order the pair holds them. */
export const VOICE_PAIR_SIDES: readonly VoicePairSideId[] = ['a', 'b']

/** The side across from one. */
export const otherSide = (side: VoicePairSideId): VoicePairSideId => (side === 'a' ? 'b' : 'a')

/** Where a side sits in `pair.sides` and in `transport.sides`. */
export const sideIndex = (side: VoicePairSideId): 0 | 1 => (side === 'a' ? 0 : 1)

/** The side that holds a voiceprint; null for none, and for a voiceprint the pair does not hold. */
export function sideOfVoiceprint(
  pair: Pick<VoicePair, 'sides'>,
  voiceprintId: string | null | undefined
): VoicePairSideId | null {
  if (!voiceprintId) return null
  return (
    VOICE_PAIR_SIDES.find(side => pair.sides[sideIndex(side)].voiceprint.id === voiceprintId) ??
    null
  )
}

// ── Listening ────────────────────────────────────────────────────────────────

/** One side under the transport. */
export interface VoicePairSideState {
  /**
   * Whether its clip can be played: nothing fetched yet (the first play fetches it, and the
   * fetch is recorded), on its way, here, or not playable in this browser or not there at all.
   */
  audio: RecordingAudioState
  /** Where the side stands: each keeps its own place, and `playing` is only the one that sounds. */
  playback: PlaybackState
  /** Listened to in this visit. */
  heard: boolean
}

export interface VoicePairTransportActions {
  /** Put the other side in the transport; it goes on from where that side was left. */
  onSelect: (side: VoicePairSideId) => void
  onTogglePlay: () => void
  /** Move one side; the start of the active one is its *From the start*. */
  onSeek: (side: VoicePairSideId, ms: number) => void
}

/** One transport, two sources: the side in it, how each side stands, and its controls. */
export interface VoicePairTransport {
  active: VoicePairSideId
  sides: readonly [VoicePairSideState, VoicePairSideState]
  actions: VoicePairTransportActions
  /** Put the keyboard in the transport when the view opens: a desktop host asks for it. */
  autoFocus?: boolean
}

/** What a decision still waits for: both sides, the one side that can be played, or nothing. */
export type ListeningOwed = 'both' | 'playable' | null

/**
 * Whether a decision still waits for listening: every side that can be played has to be heard in
 * this visit first. A side with nothing to play owes nothing, so a pair whose clips are both
 * gone can still be judged on what was read.
 */
export function listeningOwed(sides: VoicePairTransport['sides']): ListeningOwed {
  const playable = sides.filter(side => side.audio !== 'unavailable')
  if (playable.every(side => side.heard)) return null
  return playable.length === sides.length ? 'both' : 'playable'
}

// ── Deciding ─────────────────────────────────────────────────────────────────

export type VoicePairDecision = 'same' | 'different'

/**
 * Why a decision, a reopening or a merge was refused: the operator may not decide, the pair is
 * gone, changed or already decided, its two persons were met together or are one by now, the
 * change could not be recorded in the audit trail, no connection, or anything else.
 */
export type VoicePairChangeProblem =
  | 'forbidden'
  | 'not_found'
  | 'changed'
  | 'decided'
  | 'seen_together'
  | 'same_owner'
  | 'audit_unavailable'
  | 'offline'
  | 'error'

/**
 * Decisions on a pair. The host gives them only to a collaborator on a desktop; without them the
 * view states where the pair stands and binds no decision key.
 */
export interface VoicePairDecisionActions {
  /** A change is on its way: no other is taken until it is answered. */
  busy?: boolean
  /** Why the last change was refused; said in the panel until the next one is sent. */
  problem?: VoicePairChangeProblem | null
  onDecide: (pairId: string, decision: VoicePairDecision, note: string) => void
  /** Reopen a decided pair (audited like the decision itself). */
  onReopen: (pairId: string) => void
  /**
   * Merge the two persons; the person of `survivor`'s side stays (audited, reversible). The
   * analyst picks the side in the merge question.
   */
  onMerge: (pairId: string, survivor: VoicePairSideId) => void
  /**
   * The side whose person a merge keeps unless the analyst swaps it: the subject the comparison
   * was opened from. Side A when the host names none — the sides are ordered by voiceprint id,
   * which says nothing of who should stay.
   */
  defaultSurvivor?: VoicePairSideId
}

/** Why the view decides nothing: the operator only reads the case, or the screen is a phone. */
export type VoicePairReadOnly = 'role' | 'phone'

/**
 * Where a pair stands, as the analyst reads it. The same voice confirmed on two persons who were
 * never met together raises the question of a merge: a similar voice is not one person.
 */
export type VoicePairOutcome =
  | 'pending'
  | 'different'
  | 'same'
  | 'sameSeenTogether'
  | 'mergeProposal'

/** Whether one person holds both voiceprints by now: such a pair is never reopened. */
export const oneOwner = (pair: Pick<VoicePair, 'sides'>): boolean =>
  pair.sides[0].owner.id === pair.sides[1].owner.id

export function voicePairOutcome(
  pair: Pick<VoicePair, 'status' | 'seenTogether' | 'sides'>
): VoicePairOutcome {
  if (pair.status === 'suggested') return 'pending'
  if (pair.status === 'rejected') return 'different'
  if (oneOwner(pair)) return 'same'
  return pair.seenTogether ? 'sameSeenTogether' : 'mergeProposal'
}

// ── Reading ──────────────────────────────────────────────────────────────────

/** Why the comparison could not be read, as any audited read of the workspace says it. */
export type VoicePairReadProblem = SubjectPageProblem

/** The comparison as its page holds it: being read, refused, or here. */
export type VoicePairRead =
  | { status: 'loading' }
  | { status: 'failed'; problem: VoicePairReadProblem; retrying?: boolean }
  | { status: 'ready'; pair: VoicePair }
